import React, { useEffect, useState, useRef } from 'react';
import api from '@/utils/api';
import { Palette, Image, Type, Save, RefreshCw, Eye, Undo, Upload, ImageIcon, LayoutTemplate, Truck, Phone } from 'lucide-react';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { useTenant } from '@/contexts/TenantContext';

const DEFAULT_COLORS = {
  primary_color: '#4A6741',
  accent_color: '#D4A55A',
  header_bg_color: '#FFFFFF'
};

const DEFAULT_BANNER = 'https://customer-assets.emergentagent.com/job_impact-golf-shops/artifacts/2yfem0xb_HomeBanner.png';

const COLOR_PRESETS = [
  { name: 'Forest Green', primary: '#4A6741', accent: '#D4A55A' },
  { name: 'Navy Blue', primary: '#1E3A5F', accent: '#E8B923' },
  { name: 'Classic Black', primary: '#1F2937', accent: '#EF4444' },
  { name: 'Royal Purple', primary: '#5B21B6', accent: '#F59E0B' },
  { name: 'Ocean Teal', primary: '#0D9488', accent: '#F97316' },
];

export default function AdminBranding() {
  const { refreshBranding } = useTenant();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingBanner, setUploadingBanner] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingStorefront, setUploadingStorefront] = useState(false);
  const bannerInputRef = useRef(null);
  const logoInputRef = useRef(null);
  const storefrontInputRef = useRef(null);
  const [branding, setBranding] = useState({
    name: '',
    slug: '',
    logo_url: '',
    banner_image_url: '',
    storefront_preview_url: '',
    primary_color: DEFAULT_COLORS.primary_color,
    accent_color: DEFAULT_COLORS.accent_color,
    header_bg_color: DEFAULT_COLORS.header_bg_color,
    font_family: 'Chivo, sans-serif',
    // Homepage text fields
    hero_title: '',
    hero_subtitle: '',
    hero_cta_text: '',
    categories_title: '',
    categories_subtitle: '',
    features_title: '',
    features_subtitle: '',
    cta_title: '',
    cta_subtitle: '',
    // Header display customization
    hide_header_logo: false,
    hide_header_name: false,
    header_tagline: '',
    // Shipping
    in_store_pickup_enabled: false,
    // Contact
    contact_phone: ''
  });
  const [originalBranding, setOriginalBranding] = useState(null);

  useEffect(() => {
    loadBranding();
  }, []);

  const loadBranding = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/branding');
      const data = {
        name: response.data.name || '',
        slug: response.data.slug || '',
        logo_url: response.data.logo_url || '',
        banner_image_url: response.data.banner_image_url || '',
        storefront_preview_url: response.data.storefront_preview_url || '',
        primary_color: response.data.primary_color || DEFAULT_COLORS.primary_color,
        accent_color: response.data.accent_color || DEFAULT_COLORS.accent_color,
        header_bg_color: response.data.header_bg_color || DEFAULT_COLORS.header_bg_color,
        font_family: response.data.font_family || 'Chivo, sans-serif',
        // Homepage text fields
        hero_title: response.data.hero_title || '',
        hero_subtitle: response.data.hero_subtitle || '',
        hero_cta_text: response.data.hero_cta_text || '',
        categories_title: response.data.categories_title || '',
        categories_subtitle: response.data.categories_subtitle || '',
        features_title: response.data.features_title || '',
        features_subtitle: response.data.features_subtitle || '',
        cta_title: response.data.cta_title || '',
        cta_subtitle: response.data.cta_subtitle || '',
        // Header display customization
        hide_header_logo: response.data.hide_header_logo || false,
        hide_header_name: response.data.hide_header_name || false,
        header_tagline: response.data.header_tagline || '',
        header_tagline_locked: response.data.header_tagline_locked || false,
        // Shipping
        in_store_pickup_enabled: response.data.in_store_pickup_enabled || false,
        // Contact
        contact_phone: response.data.contact_phone || ''
      };
      setBranding(data);
      setOriginalBranding(data);
    } catch (error) {
      toast.error('Failed to load branding settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      
      // Validate hex colors
      const colorFields = ['primary_color', 'accent_color', 'header_bg_color'];
      for (const field of colorFields) {
        if (branding[field] && !/^#[0-9A-Fa-f]{6}$/.test(branding[field])) {
          toast.error(`Invalid ${field.replace(/_/g, ' ')} format. Use hex format: #RRGGBB`);
          return;
        }
      }

      await api.put('/admin/branding', {
        logo_url: branding.logo_url || null,
        banner_image_url: branding.banner_image_url || null,
        storefront_preview_url: branding.storefront_preview_url || null,
        primary_color: branding.primary_color,
        accent_color: branding.accent_color,
        header_bg_color: branding.header_bg_color,
        font_family: branding.font_family,
        // Homepage text fields
        hero_title: branding.hero_title,
        hero_subtitle: branding.hero_subtitle,
        hero_cta_text: branding.hero_cta_text,
        categories_title: branding.categories_title,
        categories_subtitle: branding.categories_subtitle,
        features_title: branding.features_title,
        features_subtitle: branding.features_subtitle,
        cta_title: branding.cta_title,
        cta_subtitle: branding.cta_subtitle,
        // Header display customization
        hide_header_logo: branding.hide_header_logo,
        hide_header_name: branding.hide_header_name,
        header_tagline: branding.header_tagline,
        // Shipping
        in_store_pickup_enabled: branding.in_store_pickup_enabled,
        // Contact
        contact_phone: branding.contact_phone
      });

      setOriginalBranding({ ...branding });
      toast.success('Branding updated successfully');
      
      // Refresh branding in context so Header updates
      if (refreshBranding) {
        refreshBranding();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save branding');
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = async (file, imageType) => {
    const setUploading = imageType === 'banner' ? setUploadingBanner : 
                         imageType === 'storefront' ? setUploadingStorefront : setUploadingLogo;
    
    try {
      setUploading(true);
      
      const formData = new FormData();
      formData.append('file', file);
      formData.append('image_type', imageType);
      
      const response = await api.post('/admin/upload-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const fieldName = imageType === 'banner' ? 'banner_image_url' : 
                        imageType === 'storefront' ? 'storefront_preview_url' : 'logo_url';
      setBranding(prev => ({ ...prev, [fieldName]: response.data.url }));
      toast.success(`${imageType.charAt(0).toUpperCase() + imageType.slice(1)} uploaded successfully`);
      
      // Refresh branding
      if (refreshBranding) {
        refreshBranding();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to upload ${imageType}`);
    } finally {
      setUploading(false);
    }
  };

  const handleBannerFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      handleImageUpload(file, 'banner');
    }
  };

  const handleLogoFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      handleImageUpload(file, 'logo');
    }
  };

  const handleStorefrontFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      handleImageUpload(file, 'storefront');
    }
  };

  const handleReset = () => {
    if (originalBranding) {
      setBranding({ ...originalBranding });
      toast.info('Changes reverted');
    }
  };

  const applyPreset = (preset) => {
    setBranding(prev => ({
      ...prev,
      primary_color: preset.primary,
      accent_color: preset.accent
    }));
    toast.success(`Applied "${preset.name}" preset`);
  };

  const hasChanges = JSON.stringify(branding) !== JSON.stringify(originalBranding);

  if (loading) {
    return (
      <div className="text-center py-12" data-testid="branding-loading">
        <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-[#4A6741] border-r-transparent"></div>
        <p className="mt-4 text-gray-600">Loading branding settings...</p>
      </div>
    );
  }

  return (
    <div data-testid="admin-branding-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Store Branding
          </h2>
          <p className="text-gray-500 mt-1">Customize your store's appearance</p>
        </div>
        <div className="flex items-center space-x-3">
          {hasChanges && (
            <Button
              variant="outline"
              onClick={handleReset}
              data-testid="reset-branding-btn"
            >
              <Undo className="h-4 w-4 mr-2" />
              Revert
            </Button>
          )}
          <Button
            onClick={handleSave}
            disabled={saving || !hasChanges}
            className="bg-[#4A6741] hover:bg-[#3d5636]"
            data-testid="save-branding-btn"
          >
            {saving ? (
              <>
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Save Changes
              </>
            )}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Settings Panel */}
        <div className="lg:col-span-2 space-y-6">
          {/* Store Identity */}
          <div className="bg-white rounded-lg border border-[#E8E4DC] p-6">
            <div className="flex items-center mb-4">
              <Type className="h-5 w-5 text-[#4A6741] mr-2" />
              <h3 className="text-lg font-bold text-gray-900">Store Identity</h3>
            </div>
            
            <div className="space-y-4">
              <div>
                <Label className="text-gray-700">Store Name</Label>
                <p className="text-sm text-gray-500 mb-2">This is your tenant name and will appear in the header</p>
                <Input
                  value={branding.name}
                  disabled
                  className="bg-gray-100 border-[#E8E4DC]"
                  data-testid="store-name-display"
                />
                <p className="text-xs text-gray-400 mt-1">Contact support to change your store name</p>
              </div>

              <div>
                <Label className="text-gray-700">Store URL</Label>
                <div className="flex items-center mt-1">
                  <span className="text-gray-500 text-sm bg-gray-100 px-3 py-2 rounded-l-md border border-r-0 border-[#E8E4DC]">
                    /
                  </span>
                  <Input
                    value={branding.slug}
                    disabled
                    className="bg-gray-100 border-[#E8E4DC] rounded-l-none"
                    data-testid="store-slug-display"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Logo */}
          <div className="bg-white rounded-lg border border-[#E8E4DC] p-6">
            <div className="flex items-center mb-4">
              <Image className="h-5 w-5 text-[#4A6741] mr-2" />
              <h3 className="text-lg font-bold text-gray-900">Logo</h3>
            </div>

            <div className="space-y-4">
              <div>
                <Label htmlFor="logo_url">Logo URL or Upload</Label>
                <p className="text-sm text-gray-500 mb-2">Enter a URL or upload an image (recommended: 200x200px, PNG or SVG, max 500KB)</p>
                <div className="flex gap-2">
                  <Input
                    id="logo_url"
                    value={branding.logo_url}
                    onChange={(e) => setBranding(prev => ({ ...prev, logo_url: e.target.value }))}
                    placeholder="https://example.com/your-logo.png"
                    className="bg-gray-50 border-[#E8E4DC] flex-1"
                    data-testid="logo-url-input"
                  />
                  <input
                    type="file"
                    ref={logoInputRef}
                    onChange={handleLogoFileChange}
                    accept="image/jpeg,image/png,image/webp,image/gif"
                    className="hidden"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => logoInputRef.current?.click()}
                    disabled={uploadingLogo}
                    data-testid="upload-logo-btn"
                  >
                    {uploadingLogo ? (
                      <RefreshCw className="h-4 w-4 animate-spin" />
                    ) : (
                      <Upload className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>

              {branding.logo_url && (
                <div className="p-4 bg-gray-50 rounded-lg border border-dashed border-[#E8E4DC]">
                  <p className="text-xs text-gray-500 mb-2">Logo Preview:</p>
                  <img 
                    src={branding.logo_url} 
                    alt="Logo preview" 
                    className="h-16 w-16 object-contain rounded"
                    onError={(e) => e.target.style.display = 'none'}
                    data-testid="logo-preview"
                  />
                </div>
              )}
            </div>
          </div>

          {/* Header Display */}
          <div className="bg-white rounded-lg border border-[#E8E4DC] p-6">
            <div className="flex items-center mb-4">
              <LayoutTemplate className="h-5 w-5 text-[#4A6741] mr-2" />
              <h3 className="text-lg font-bold text-gray-900">Header Display</h3>
            </div>
            <p className="text-sm text-gray-500 mb-4">Control what shows next to your logo in the storefront header.</p>

            {branding.header_tagline_locked && (
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 mb-4" data-testid="header-tagline-locked-notice">
                Fulfillment stores always show "Powered by Impact Golf Center" with your logo and name visible - this is set platform-wide and can't be changed here.
              </p>
            )}

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-gray-700">Hide Logo</Label>
                  <p className="text-xs text-gray-500">Don't show the logo image in the header</p>
                </div>
                <Switch
                  checked={branding.hide_header_logo}
                  disabled={branding.header_tagline_locked}
                  onCheckedChange={(checked) => setBranding(prev => ({ ...prev, hide_header_logo: checked }))}
                  data-testid="hide-header-logo-switch"
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-gray-700">Hide Store Name</Label>
                  <p className="text-xs text-gray-500">Don't show the store name text in the header</p>
                </div>
                <Switch
                  checked={branding.hide_header_name}
                  disabled={branding.header_tagline_locked}
                  onCheckedChange={(checked) => setBranding(prev => ({ ...prev, hide_header_name: checked }))}
                  data-testid="hide-header-name-switch"
                />
              </div>
              <div>
                <Label htmlFor="header_tagline">Header Tagline</Label>
                <p className="text-xs text-gray-500 mb-2">Optional text shown in the header (e.g. "Powered by Impact Golf Center")</p>
                <Input
                  id="header_tagline"
                  value={branding.header_tagline}
                  disabled={branding.header_tagline_locked}
                  onChange={(e) => setBranding(prev => ({ ...prev, header_tagline: e.target.value }))}
                  placeholder="Powered by..."
                  className="bg-white border-[#E8E4DC] disabled:opacity-60 disabled:cursor-not-allowed"
                  data-testid="header-tagline-input"
                />
              </div>
            </div>
          </div>

          {/* Contact */}
          <div className="bg-white rounded-lg border border-[#E8E4DC] p-6">
            <div className="flex items-center mb-4">
              <Phone className="h-5 w-5 text-[#4A6741] mr-2" />
              <h3 className="text-lg font-bold text-gray-900">Contact Information</h3>
            </div>
            <p className="text-sm text-gray-500 mb-4">
              Shown to customers who need to call for pricing (e.g. on brands currently marked "Call for Pricing") or for general support.
            </p>
            <div>
              <Label htmlFor="contact_phone">Phone Number</Label>
              <Input
                id="contact_phone"
                type="tel"
                value={branding.contact_phone}
                onChange={(e) => setBranding(prev => ({ ...prev, contact_phone: e.target.value }))}
                placeholder="e.g., (555) 123-4567"
                className="mt-2 bg-white border-[#E8E4DC]"
                data-testid="contact-phone-input"
              />
            </div>
          </div>

          {/* Shipping */}
          <div className="bg-white rounded-lg border border-[#E8E4DC] p-6">
            <div className="flex items-center mb-4">
              <Truck className="h-5 w-5 text-[#4A6741] mr-2" />
              <h3 className="text-lg font-bold text-gray-900">Shipping</h3>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-gray-700">In-Store Pickup</Label>
                <p className="text-xs text-gray-500">Let local customers choose free in-store pickup at checkout</p>
              </div>
              <Switch
                checked={branding.in_store_pickup_enabled}
                onCheckedChange={(checked) => setBranding(prev => ({ ...prev, in_store_pickup_enabled: checked }))}
                data-testid="in-store-pickup-switch"
              />
            </div>
          </div>

          {/* Banner Image */}
          <div className="bg-white rounded-lg border border-[#E8E4DC] p-6">
            <div className="flex items-center mb-4">
              <ImageIcon className="h-5 w-5 text-[#4A6741] mr-2" />
              <h3 className="text-lg font-bold text-gray-900">Homepage Banner</h3>
            </div>

            <div className="space-y-4">
              <div>
                <Label htmlFor="banner_image_url">Banner Image URL or Upload</Label>
                <p className="text-sm text-gray-500 mb-2">Hero banner displayed on your homepage (recommended: 1920x1080px, max 2MB)</p>
                <div className="flex gap-2">
                  <Input
                    id="banner_image_url"
                    value={branding.banner_image_url}
                    onChange={(e) => setBranding(prev => ({ ...prev, banner_image_url: e.target.value }))}
                    placeholder="https://example.com/your-banner.jpg"
                    className="bg-gray-50 border-[#E8E4DC] flex-1"
                    data-testid="banner-url-input"
                  />
                  <input
                    type="file"
                    ref={bannerInputRef}
                    onChange={handleBannerFileChange}
                    accept="image/jpeg,image/png,image/webp,image/gif"
                    className="hidden"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => bannerInputRef.current?.click()}
                    disabled={uploadingBanner}
                    data-testid="upload-banner-btn"
                  >
                    {uploadingBanner ? (
                      <RefreshCw className="h-4 w-4 animate-spin" />
                    ) : (
                      <Upload className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>

              <div className="p-4 bg-gray-50 rounded-lg border border-dashed border-[#E8E4DC]">
                <p className="text-xs text-gray-500 mb-2">Banner Preview:</p>
                <img 
                  src={branding.banner_image_url || DEFAULT_BANNER} 
                  alt="Banner preview" 
                  className="w-full h-32 object-cover rounded"
                  onError={(e) => { e.target.src = DEFAULT_BANNER; }}
                  data-testid="banner-preview"
                />
                {!branding.banner_image_url && (
                  <p className="text-xs text-gray-400 mt-2">Using default banner. Upload a custom image to personalize your store.</p>
                )}
              </div>
            </div>
          </div>

          {/* Storefront Preview */}
          <div className="bg-white rounded-lg border border-[#E8E4DC] p-6">
            <div className="flex items-center mb-4">
              <Eye className="h-5 w-5 text-[#4A6741] mr-2" />
              <h3 className="text-lg font-bold text-gray-900">Storefront Preview</h3>
            </div>

            <div className="space-y-4">
              <div>
                <Label htmlFor="storefront_preview_url">Homepage Screenshot</Label>
                <p className="text-sm text-gray-500 mb-2">
                  Upload a screenshot of your store's homepage. This will be displayed in the "Featured Stores" section on the platform homepage. 
                  (Recommended: 1920x1080px or similar widescreen ratio, max 3MB)
                </p>
                <div className="flex gap-2">
                  <Input
                    id="storefront_preview_url"
                    value={branding.storefront_preview_url}
                    onChange={(e) => setBranding(prev => ({ ...prev, storefront_preview_url: e.target.value }))}
                    placeholder="https://example.com/your-screenshot.png"
                    className="bg-gray-50 border-[#E8E4DC] flex-1"
                    data-testid="storefront-url-input"
                  />
                  <input
                    type="file"
                    ref={storefrontInputRef}
                    onChange={handleStorefrontFileChange}
                    accept="image/jpeg,image/png,image/webp,image/gif"
                    className="hidden"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => storefrontInputRef.current?.click()}
                    disabled={uploadingStorefront}
                    data-testid="upload-storefront-btn"
                  >
                    {uploadingStorefront ? (
                      <RefreshCw className="h-4 w-4 animate-spin" />
                    ) : (
                      <Upload className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>

              <div className="p-4 bg-gray-50 rounded-lg border border-dashed border-[#E8E4DC]">
                <p className="text-xs text-gray-500 mb-2">Storefront Preview:</p>
                {branding.storefront_preview_url ? (
                  <img 
                    src={branding.storefront_preview_url} 
                    alt="Storefront preview" 
                    className="w-full h-48 object-cover object-top rounded"
                    data-testid="storefront-preview"
                  />
                ) : (
                  <div className="w-full h-48 bg-gray-200 rounded flex flex-col items-center justify-center text-gray-400">
                    <ImageIcon className="h-12 w-12 mb-2" />
                    <p className="text-sm">No storefront preview uploaded</p>
                    <p className="text-xs mt-1">Take a screenshot of your homepage and upload it here</p>
                  </div>
                )}
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  <strong>Tip:</strong> To capture a screenshot of your homepage, press <kbd className="px-1 py-0.5 bg-blue-100 rounded text-xs">Cmd+Shift+4</kbd> (Mac) or <kbd className="px-1 py-0.5 bg-blue-100 rounded text-xs">Win+Shift+S</kbd> (Windows), then select the area you want to capture.
                </p>
              </div>
            </div>
          </div>

          {/* Homepage Text */}
          <div className="bg-white rounded-lg border border-[#E8E4DC] p-6">
            <div className="flex items-center mb-4">
              <Type className="h-5 w-5 text-[#4A6741] mr-2" />
              <h3 className="text-lg font-bold text-gray-900">Homepage Text</h3>
            </div>
            <p className="text-sm text-gray-500 mb-6">Customize the text displayed on your store's homepage sections. Leave blank to use defaults.</p>

            {/* Hero Section */}
            <div className="mb-6 p-4 bg-gray-50 rounded-lg">
              <h4 className="text-sm font-bold text-gray-700 mb-3 uppercase tracking-wider">Hero Banner</h4>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="hero_title">Hero Title</Label>
                  <Input
                    id="hero_title"
                    value={branding.hero_title}
                    onChange={(e) => setBranding(prev => ({ ...prev, hero_title: e.target.value }))}
                    placeholder="PRECISION ENGINEERED"
                    className="bg-white border-[#E8E4DC]"
                    data-testid="hero-title-input"
                  />
                </div>
                <div>
                  <Label htmlFor="hero_subtitle">Hero Subtitle</Label>
                  <Input
                    id="hero_subtitle"
                    value={branding.hero_subtitle}
                    onChange={(e) => setBranding(prev => ({ ...prev, hero_subtitle: e.target.value }))}
                    placeholder="Custom golf equipment built to your exact specifications..."
                    className="bg-white border-[#E8E4DC]"
                    data-testid="hero-subtitle-input"
                  />
                </div>
                <div>
                  <Label htmlFor="hero_cta_text">Hero Button Text</Label>
                  <Input
                    id="hero_cta_text"
                    value={branding.hero_cta_text}
                    onChange={(e) => setBranding(prev => ({ ...prev, hero_cta_text: e.target.value }))}
                    placeholder="Shop Custom"
                    className="bg-white border-[#E8E4DC]"
                    data-testid="hero-cta-input"
                  />
                </div>
              </div>
            </div>

            {/* Categories Section */}
            <div className="mb-6 p-4 bg-gray-50 rounded-lg">
              <h4 className="text-sm font-bold text-gray-700 mb-3 uppercase tracking-wider">Categories Section</h4>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="categories_title">Categories Title</Label>
                  <Input
                    id="categories_title"
                    value={branding.categories_title}
                    onChange={(e) => setBranding(prev => ({ ...prev, categories_title: e.target.value }))}
                    placeholder="Explore Categories"
                    className="bg-white border-[#E8E4DC]"
                    data-testid="categories-title-input"
                  />
                </div>
                <div>
                  <Label htmlFor="categories_subtitle">Categories Subtitle</Label>
                  <Input
                    id="categories_subtitle"
                    value={branding.categories_subtitle}
                    onChange={(e) => setBranding(prev => ({ ...prev, categories_subtitle: e.target.value }))}
                    placeholder="Find the perfect equipment for your game"
                    className="bg-white border-[#E8E4DC]"
                    data-testid="categories-subtitle-input"
                  />
                </div>
              </div>
            </div>

            {/* Features Section */}
            <div className="mb-6 p-4 bg-gray-50 rounded-lg">
              <h4 className="text-sm font-bold text-gray-700 mb-3 uppercase tracking-wider">Features Section</h4>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="features_title">Features Title</Label>
                  <Input
                    id="features_title"
                    value={branding.features_title}
                    onChange={(e) => setBranding(prev => ({ ...prev, features_title: e.target.value }))}
                    placeholder="Why Choose Us"
                    className="bg-white border-[#E8E4DC]"
                    data-testid="features-title-input"
                  />
                </div>
                <div>
                  <Label htmlFor="features_subtitle">Features Subtitle</Label>
                  <Input
                    id="features_subtitle"
                    value={branding.features_subtitle}
                    onChange={(e) => setBranding(prev => ({ ...prev, features_subtitle: e.target.value }))}
                    placeholder="Golfshop.fit difference"
                    className="bg-white border-[#E8E4DC]"
                    data-testid="features-subtitle-input"
                  />
                </div>
              </div>
            </div>

            {/* CTA Section */}
            <div className="p-4 bg-gray-50 rounded-lg">
              <h4 className="text-sm font-bold text-gray-700 mb-3 uppercase tracking-wider">Call-to-Action Section</h4>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="cta_title">CTA Title</Label>
                  <Input
                    id="cta_title"
                    value={branding.cta_title}
                    onChange={(e) => setBranding(prev => ({ ...prev, cta_title: e.target.value }))}
                    placeholder="Ready to Elevate Your Game?"
                    className="bg-white border-[#E8E4DC]"
                    data-testid="cta-title-input"
                  />
                </div>
                <div>
                  <Label htmlFor="cta_subtitle">CTA Subtitle</Label>
                  <Input
                    id="cta_subtitle"
                    value={branding.cta_subtitle}
                    onChange={(e) => setBranding(prev => ({ ...prev, cta_subtitle: e.target.value }))}
                    placeholder="Browse our collection of custom shafts and equipment..."
                    className="bg-white border-[#E8E4DC]"
                    data-testid="cta-subtitle-input"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Colors */}
          <div className="bg-white rounded-lg border border-[#E8E4DC] p-6">
            <div className="flex items-center mb-4">
              <Palette className="h-5 w-5 text-[#4A6741] mr-2" />
              <h3 className="text-lg font-bold text-gray-900">Colors</h3>
            </div>

            {/* Color Presets */}
            <div className="mb-6">
              <Label className="text-gray-700 mb-2 block">Quick Presets</Label>
              <div className="flex flex-wrap gap-2">
                {COLOR_PRESETS.map((preset) => (
                  <button
                    key={preset.name}
                    onClick={() => applyPreset(preset)}
                    className="flex items-center space-x-2 px-3 py-2 rounded-lg border border-[#E8E4DC] hover:border-[#4A6741] transition-colors"
                    data-testid={`preset-${preset.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <div className="flex">
                      <div 
                        className="w-4 h-4 rounded-l" 
                        style={{ backgroundColor: preset.primary }}
                      />
                      <div 
                        className="w-4 h-4 rounded-r" 
                        style={{ backgroundColor: preset.accent }}
                      />
                    </div>
                    <span className="text-sm text-gray-700">{preset.name}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="primary_color">Primary Color</Label>
                <p className="text-xs text-gray-500 mb-2">Main brand color (buttons, links)</p>
                <div className="flex items-center space-x-2">
                  <input
                    type="color"
                    value={branding.primary_color}
                    onChange={(e) => setBranding(prev => ({ ...prev, primary_color: e.target.value }))}
                    className="w-10 h-10 rounded border border-[#E8E4DC] cursor-pointer"
                    data-testid="primary-color-picker"
                  />
                  <Input
                    id="primary_color"
                    value={branding.primary_color}
                    onChange={(e) => setBranding(prev => ({ ...prev, primary_color: e.target.value }))}
                    placeholder="#4A6741"
                    className="bg-gray-50 border-[#E8E4DC] font-mono text-sm"
                    data-testid="primary-color-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="accent_color">Accent Color</Label>
                <p className="text-xs text-gray-500 mb-2">Highlights and CTAs</p>
                <div className="flex items-center space-x-2">
                  <input
                    type="color"
                    value={branding.accent_color}
                    onChange={(e) => setBranding(prev => ({ ...prev, accent_color: e.target.value }))}
                    className="w-10 h-10 rounded border border-[#E8E4DC] cursor-pointer"
                    data-testid="accent-color-picker"
                  />
                  <Input
                    id="accent_color"
                    value={branding.accent_color}
                    onChange={(e) => setBranding(prev => ({ ...prev, accent_color: e.target.value }))}
                    placeholder="#D4A55A"
                    className="bg-gray-50 border-[#E8E4DC] font-mono text-sm"
                    data-testid="accent-color-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="header_bg_color">Header Background</Label>
                <p className="text-xs text-gray-500 mb-2">Header background color</p>
                <div className="flex items-center space-x-2">
                  <input
                    type="color"
                    value={branding.header_bg_color}
                    onChange={(e) => setBranding(prev => ({ ...prev, header_bg_color: e.target.value }))}
                    className="w-10 h-10 rounded border border-[#E8E4DC] cursor-pointer"
                    data-testid="header-bg-color-picker"
                  />
                  <Input
                    id="header_bg_color"
                    value={branding.header_bg_color}
                    onChange={(e) => setBranding(prev => ({ ...prev, header_bg_color: e.target.value }))}
                    placeholder="#FFFFFF"
                    className="bg-gray-50 border-[#E8E4DC] font-mono text-sm"
                    data-testid="header-bg-color-input"
                  />
                </div>
              </div>
            </div>

            <Button
              variant="ghost"
              size="sm"
              onClick={() => setBranding(prev => ({
                ...prev,
                primary_color: DEFAULT_COLORS.primary_color,
                accent_color: DEFAULT_COLORS.accent_color,
                header_bg_color: DEFAULT_COLORS.header_bg_color
              }))}
              className="mt-4 text-gray-500"
              data-testid="reset-colors-btn"
            >
              <RefreshCw className="h-3 w-3 mr-1" />
              Reset to defaults
            </Button>
          </div>
        </div>

        {/* Live Preview Panel */}
        <div className="lg:col-span-1">
          <div className="sticky top-24">
            <div className="bg-white rounded-lg border border-[#E8E4DC] overflow-hidden">
              <div className="p-4 border-b border-[#E8E4DC]">
                <div className="flex items-center">
                  <Eye className="h-4 w-4 text-gray-500 mr-2" />
                  <h3 className="font-bold text-gray-900">Live Preview</h3>
                </div>
              </div>
              
              {/* Mini Header Preview */}
              <div 
                className="p-4 border-b"
                style={{ backgroundColor: branding.header_bg_color }}
                data-testid="header-preview"
              >
                <div className="flex items-center space-x-3">
                  {branding.logo_url ? (
                    <img 
                      src={branding.logo_url} 
                      alt="Logo" 
                      className="h-10 w-10 object-contain rounded"
                      onError={(e) => e.target.style.display = 'none'}
                    />
                  ) : (
                    <div 
                      className="h-10 w-10 rounded flex items-center justify-center text-white text-xs font-bold"
                      style={{ backgroundColor: branding.primary_color }}
                    >
                      {branding.name?.charAt(0) || 'S'}
                    </div>
                  )}
                  <div>
                    <p className="font-bold text-sm" style={{ color: branding.primary_color }}>
                      {branding.name || 'Store Name'}
                    </p>
                    <p className="text-xs text-gray-500">Golf</p>
                  </div>
                </div>
              </div>

              {/* Button Preview */}
              <div className="p-4 space-y-3">
                <p className="text-xs text-gray-500 uppercase font-bold">Buttons</p>
                <button 
                  className="w-full py-2 px-4 rounded-lg text-white font-bold text-sm"
                  style={{ backgroundColor: branding.primary_color }}
                >
                  Primary Button
                </button>
                <button 
                  className="w-full py-2 px-4 rounded-lg text-white font-bold text-sm"
                  style={{ backgroundColor: branding.accent_color }}
                >
                  Accent Button
                </button>
              </div>

              {/* Links Preview */}
              <div className="p-4 border-t border-[#E8E4DC]">
                <p className="text-xs text-gray-500 uppercase font-bold mb-2">Links & Highlights</p>
                <p className="text-sm">
                  Regular text with{' '}
                  <span style={{ color: branding.primary_color }} className="font-medium">primary link</span>
                  {' '}and{' '}
                  <span style={{ color: branding.accent_color }} className="font-medium">accent highlight</span>
                </p>
              </div>

              {/* Badge Preview */}
              <div className="p-4 border-t border-[#E8E4DC]">
                <p className="text-xs text-gray-500 uppercase font-bold mb-2">Badges</p>
                <div className="flex space-x-2">
                  <span 
                    className="px-2 py-1 rounded text-xs font-bold text-white"
                    style={{ backgroundColor: branding.primary_color }}
                  >
                    In Stock
                  </span>
                  <span 
                    className="px-2 py-1 rounded text-xs font-bold text-white"
                    style={{ backgroundColor: branding.accent_color }}
                  >
                    Sale
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
